import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, shareReplay } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  LoginOAuthParams,
  OauthAEMConfig,
  RedirectfulResponse
} from '../../models';
import { AppTokenHelperService } from './app-token-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AppTokenService extends AppTokenHelperService {
  private tokenRequests = new Map<string, Observable<AuthAccessTokenDetails>>();

  getAppTokenWithParams(
    clientId: string,
    scope: string,
    header = false
  ): Observable<AuthAccessTokenDetails> {
    return this.userData.getAuthConfigContent().pipe(
      // eslint-disable-next-line complexity
      concatMap((config: OauthAEMConfig) => {
        const updatedConfig: OauthAEMConfig = {
          ...config,
          CLIENT_ID_VALUE: header
            ? config.HEADER_CLIENT_ID_VALUE || ''
            : clientId || config.CLIENT_ID_VALUE,
          SCOPE_VALUE: header
            ? `${config.SCOPE_VALUE} ${config.SAVED_SEARCH_GRAPHQL_SCOPE_VALUE}` ||
              ''
            : scope || config.SCOPE_VALUE,
          SCOPE_VALUE_RM: header
            ? `${config.SCOPE_VALUE} ${config.SAVED_SEARCH_GRAPHQL_SCOPE_VALUE}` ||
              ''
            : scope || config.SCOPE_VALUE_RM
        };
        return this.getAppToken(updatedConfig);
      }),
      shareReplay(1)
    );
  }

  // eslint-disable-next-line complexity
  getAppToken(config: OauthAEMConfig): Observable<AuthAccessTokenDetails> {
    const token = this.getExistingToken(config);

    if (token && token.access_token && this.isTokenValid(config, token)) {
      return of(token);
    }

    const clientId = config.CLIENT_ID_VALUE;
    const existingRequest = this.tokenRequests.get(clientId);

    if (existingRequest) {
      return existingRequest;
    }

    const request$ = from(this.isHeaderTokenAvailable()).pipe(
      concatMap((response: AuthAccessTokenDetails) => {
        if (
          response?.access_token &&
          config.CLIENT_ID_VALUE === config.HEADER_CLIENT_ID_VALUE &&
          this.isTokenValid(config, response)
        ) {
          this.tokenMap.set(config.CLIENT_ID_VALUE, response);
          return of(response as AuthAccessTokenDetails);
        } else {
          const pkceCode = this.loginRememberMeUtils.generateCodeChallenge();
          const loginOAuthParams: LoginOAuthParams = {
            oauthConfig: config,
            pkceCode: pkceCode
          };
          return this.loginRememberMeWrapper
            .initiatePingFlow(
              loginOAuthParams,
              'INITIATE_LOGIN',
              undefined,
              true
            )
            .pipe(
              concatMap(
                (
                  response:
                    | AuthAccessTokenDetails
                    | RedirectfulResponse
                    | boolean
                    | string
                ) => {
                  if (
                    typeof response === 'string' ||
                    typeof response === 'boolean'
                  ) {
                    return of({} as AuthAccessTokenDetails);
                  } else if ('access_token' in response) {
                    this.tokenMap.set(config.CLIENT_ID_VALUE, response);
                    this.sendHeaderTokenServiceWorker(config, response);
                    return of(response);
                  } else {
                    return of({} as AuthAccessTokenDetails);
                  }
                }
              ),
              catchError((error) => {
                this.logging.log(error);
                return of({} as AuthAccessTokenDetails);
              })
            );
        }
      }),
      shareReplay(1)
    );

    this.tokenRequests.set(clientId, request$);
    request$.subscribe({
      complete: () => this.tokenRequests.delete(clientId),
      error: () => this.tokenRequests.delete(clientId)
    });

    return request$;
  }
}
